import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { ChatMessage } from "../hooks/useChat.js";
import { renderMarkdown } from "../lib/markdown.js";
import { t } from "../lib/i18n.js";
import { timeAgo } from "../lib/time-ago.js";
import { BotIcon } from "./BotIcon.js";
import { TypingIndicator } from "./TypingIndicator.js";
import { SourceCitations } from "./SourceCitations.js";

interface MessageListProps {
  messages: ChatMessage[];
  streaming: boolean;
  botName: string;
  botAvatar?: string | null;
  language?: string;
  onRetry?: () => void;
  startersSlot?: ComponentChildren;
  csatSlot?: ComponentChildren;
  children?: ComponentChildren;
}

export function MessageList({ messages, streaming, botName, botAvatar, language = "en", onRetry, startersSlot, csatSlot, children }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, children]);

  // Find the index of the last bot message (for CSAT placement)
  let lastBotIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant" && !messages[i].streaming) {
      lastBotIdx = i;
      break;
    }
  }

  return (
    <div role="log" aria-live="polite" aria-label="Chat messages" class="message-list flex-1 overflow-y-auto flex flex-col gap-4 px-4 pt-4 pb-2">
      {messages.map((msg, idx) => (
        <div key={msg.id}>
        <div
          class={`flex gap-3 items-start animate-fadeIn ${msg.role === "user" ? "justify-end" : "justify-start"}`}
        >
          {msg.role === "assistant" && (
            <div class="shrink-0 size-8 mt-2 rounded-full bg-[var(--primary)] flex items-center justify-center overflow-hidden">
              {botAvatar ? (
                <img src={botAvatar} alt="" class="size-8 rounded-full object-cover" />
              ) : (
                <BotIcon class="size-4 text-[var(--primary-foreground)]" />
              )}
            </div>
          )}
          <div
            class={`rounded-lg px-4 py-3 text-sm max-w-[80%] break-words ${
              msg.role === "user"
                ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                : "bg-[var(--muted)] text-[var(--foreground)]"
            }`}
          >
            {msg.role === "assistant" ? (
              msg.streaming && !msg.content ? (
                <TypingIndicator />
              ) : (
                <>
                  <div
                    class="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(msg.content),
                    }}
                  />
                  {msg.error && onRetry && (
                    <button
                      type="button"
                      onClick={onRetry}
                      class="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--primary)] hover:underline bg-transparent border-none cursor-pointer p-0"
                    >
                      <svg class="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                      </svg>
                      {t("error.retry", language)}
                    </button>
                  )}
                  {msg.sources && msg.sources.length > 0 && !msg.streaming && (
                    <SourceCitations sources={msg.sources} />
                  )}
                  {/* CSAT inline on last bot message — bottom-right */}
                  {idx === lastBotIdx && csatSlot && (
                    <div class="flex justify-end mt-1.5 -mb-1 -mr-1">
                      {csatSlot}
                    </div>
                  )}
                </>
              )
            ) : (
              <p class="whitespace-pre-wrap m-0">{msg.content}</p>
            )}
          </div>
          {msg.role === "user" && (
            <div class="shrink-0 size-8 mt-1.5 rounded-full bg-[var(--muted)] flex items-center justify-center">
              <svg class="size-4 text-[var(--muted-foreground)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
          )}
        </div>
        {/* Relative timestamp on last bot message */}
        {idx === lastBotIdx && msg.createdAt && (Date.now() - msg.createdAt > 60_000) && (
          <div class="pl-11 -mt-2 mb-1">
            <span class="text-[10px] text-[var(--muted-foreground)]/60">{timeAgo(msg.createdAt)}</span>
          </div>
        )}
        </div>
      ))}
      {startersSlot}
      {children && (
        <div class="pl-11 flex flex-col gap-1.5">
          {children}
        </div>
      )}
      <div ref={bottomRef} class="shrink-0" />
    </div>
  );
}
